import { useState } from "react";
import { Link } from "react-router-dom";
import { DataTable, StockIndicator, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { getLowStock } from "../../api/reportsApi";
import { createPurchaseOrderFromLowStock } from "../../api/purchaseOrdersApi";
import type { LowStockItemOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { SupplierSelect } from "../../components/SupplierSelect";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";
import { canWritePurchaseOrders } from "../../roles/permissions";

const COLUMNS: DataTableColumn<LowStockItemOut, keyof LowStockItemOut>[] = [
  { key: "sku", header: "SKU", render: (r) => r.sku },
  { key: "name", header: "Name", render: (r) => <Link to={`/stock/${r.id}`}>{r.name}</Link> },
  {
    key: "current_stock",
    header: "Stock",
    align: "right",
    render: (r) => <StockIndicator currentStock={r.current_stock} lowStockThreshold={r.low_stock_threshold} showLabel />,
  },
  { key: "low_stock_threshold", header: "Threshold", align: "right", render: (r) => r.low_stock_threshold },
];

/** `GET /reports/low-stock?branch_id=` (§10.5) — items at or below their reorder threshold. */
export function LowStockPage() {
  const { user, ensureFreshToken } = useAuth();
  const canReorder = user ? canWritePurchaseOrders(user.role) : false;
  const [branchId, setBranchId] = useState("");
  const state = useAsyncData(() => ensureFreshToken().then((token) => getLowStock(token, branchId || undefined)), [branchId]);

  const [reorderSupplierId, setReorderSupplierId] = useState("");
  const [reordering, setReordering] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);
  const [reorderResult, setReorderResult] = useState<string | null>(null);

  async function handleAutoReorder() {
    setReorderError(null);
    setReorderResult(null);
    if (!branchId) {
      setReorderError("Select a specific branch first — auto-reorder targets one branch at a time.");
      return;
    }
    setReordering(true);
    try {
      const token = await ensureFreshToken();
      const po = await createPurchaseOrderFromLowStock(token, {
        branch_id: branchId,
        supplier_id: reorderSupplierId || undefined,
      });
      setReorderResult(`Draft PO ${po.id.slice(0, 8)} created with ${po.lines.length} line(s) — review and order it from Purchase Orders.`);
    } catch (error) {
      setReorderError(error instanceof ApiError ? error.message : "Could not generate a draft PO.");
    } finally {
      setReordering(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Low Stock</h1>
          <p className="page__subtitle">Items at or below their reorder threshold — a live feed for restocking decisions.</p>
        </div>
      </div>

      {state.error ? <StatusBanner kind="error">{state.error}</StatusBanner> : null}

      <div className="filter-bar">
        <BranchSelect id="lowstock-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="All branches" />
      </div>

      {canReorder ? (
        <div className="panel">
          <h2 className="panel__title">Auto-reorder</h2>
          <p className="muted">
            Generates a <strong>DRAFT</strong> purchase order for every currently low-stock item in the selected
            branch, targeting 2× each item's low-stock threshold, priced at each item's last-known received unit
            cost (or 0.00 if never received). Nothing is ordered automatically — review and submit it from Purchase
            Orders like any other PO.
          </p>
          <div className="form-grid">
            <SupplierSelect id="reorder-supplier" label="Limit to supplier" value={reorderSupplierId} onChange={setReorderSupplierId} />
          </div>
          {reorderError ? <StatusBanner kind="error">{reorderError}</StatusBanner> : null}
          {reorderResult ? <StatusBanner kind="info">{reorderResult}</StatusBanner> : null}
          <div className="form-actions">
            <button type="button" className="sl-button sl-button--primary sl-button--md" onClick={() => void handleAutoReorder()} disabled={reordering || !branchId}>
              {reordering ? "Generating…" : "Generate draft PO from low stock"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="panel">
        {state.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={COLUMNS} rows={state.data ?? []} getRowId={(row) => row.id} emptyMessage="Nothing below threshold." caption="Low Stock" />
        )}
      </div>
    </div>
  );
}
